import { useEffect, useState, type ImgHTMLAttributes } from "react";
import { brandAssets } from "@pustak-lms/brand";

export interface BrandingArtwork {
  name?: string;
  logoUrl?: string;
  iconUrl?: string;
}

export interface BrandLogoProps extends Omit<ImgHTMLAttributes<HTMLImageElement>, "srcSet"> {
  variant?: "full" | "mark";
  tone?: "default" | "reversed" | "monochrome";
  fallbackSrc?: string;
}

/** Shared artwork, with an empty alt available for already-labelled links. */
export function BrandLogo({
  variant = "full",
  tone = "default",
  alt = "Pustak LMS",
  className,
  src,
  fallbackSrc,
  onError,
  width = variant === "full" ? 434 : 128,
  height = variant === "full" ? 176 : 128,
  ...props
}: BrandLogoProps) {
  const artwork = variant === "mark"
    ? { default: brandAssets.mark, reversed: brandAssets.markReversed, monochrome: brandAssets.markMonochrome }
    : { default: brandAssets.logo, reversed: brandAssets.logoReversed, monochrome: brandAssets.logoMonochrome };
  const fallback = fallbackSrc ?? artwork[tone];
  const [resolvedSrc, setResolvedSrc] = useState(src ?? fallback);
  useEffect(() => setResolvedSrc(src ?? fallback), [src, fallback]);

  return <img
    {...props}
    alt={alt}
    className={["brand-logo", `brand-logo-${variant}`, className].filter(Boolean).join(" ")}
    width={width}
    height={height}
    src={resolvedSrc}
    onError={(event) => {
      if (resolvedSrc !== fallback) setResolvedSrc(fallback);
      onError?.(event);
    }}
  />;
}
